import React, { useState } from 'react';
import { Phone, ChevronRight, FileText, MessageSquare, Radio } from 'lucide-react';
import { Section, Card } from '../shared/primitives';
import { StepRow, Segmented } from '../shared/protocolBits';
import { LocationRows } from '../shared/LocationList';
import { thiCvor } from '../../../data/hospitals/thiCvor';
import { DIALING, PAGERS, COVERAGE, HANDOFF, MAIN_OR_CONTACT, AIRWAY_CALL, CODES, outsideNumber } from '../../../data/hospitals/thiAirways';

const ACCENT = '#9F8D70';
const TABS = [
  { id: 'logistics', label: 'Logistics' },
  { id: 'airways', label: 'Airways & codes' },
];
const UNIT_GROUPS = ['icus', 'procedure'];
const UNIT_NAMES = (item) => item.name !== 'CVOR' && item.name !== 'Endo' && !item.name.startsWith('Non-Invasive');

/** Pagers and coverage on one segment; units, equipment and the code role on the other */
const AirwaysAndCodes = ({ onOpenEpic }) => {
  const [tab, setTab] = useState('logistics');
  const units = UNIT_GROUPS
    .flatMap((id) => thiCvor.locations.groups.find((g) => g.id === id)?.items || [])
    .filter(UNIT_NAMES);

  return (
    <div className="space-y-6">
      <Segmented items={TABS} value={tab} onChange={setTab} />

      {tab === 'logistics' && (
        <>
          <p className="px-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">{DIALING}</p>

          <Section title="Pagers">
            <Card>
              {PAGERS.map((p) => (
                <div key={p.id} className="px-4 py-3 flex items-center gap-3">
                  <Radio size={18} className="flex-shrink-0" style={{ color: ACCENT }} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{p.service}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.covers}</span>
                  </span>
                  <span className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">{p.ext}</span>
                </div>
              ))}
            </Card>
          </Section>

          <Section title="Coverage schedule">
            <Card>
              {COVERAGE.map((c) => (
                <div key={c.when} className="px-4 py-3 flex items-baseline gap-4">
                  <span className="w-20 flex-shrink-0 text-[15px] font-medium text-gray-900 dark:text-white">{c.when}</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">{c.who}</span>
                </div>
              ))}
              <StepRow items={HANDOFF} />
            </Card>
          </Section>

          <Section title="Reaching the Main OR anesthesiologist">
            <Card>
              <p className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 leading-snug">{MAIN_OR_CONTACT.intro}</p>
              {MAIN_OR_CONTACT.options.map((o) => {
                const inner = (
                  <>
                    {o.ext ? <Phone size={18} className="flex-shrink-0" style={{ color: ACCENT }} /> : <MessageSquare size={18} className="flex-shrink-0" style={{ color: ACCENT }} />}
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{o.label}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{o.detail}</span>
                    </span>
                    {o.ext && <span className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">{o.ext}</span>}
                  </>
                );
                return o.ext
                  ? <a key={o.label} href={`tel:${outsideNumber(o.ext)}`} onClick={() => {}} className="px-4 py-3 flex items-center gap-3">{inner}</a>
                  : <div key={o.label} className="px-4 py-3 flex items-center gap-3">{inner}</div>;
              })}
            </Card>
          </Section>
        </>
      )}

      {tab === 'airways' && (
        <>
          <Card>
            <button onClick={() => { onOpenEpic?.(); }} className="w-full px-4 py-3 flex items-center gap-3 text-left">
              <FileText size={18} className="flex-shrink-0" style={{ color: ACCENT }} />
              <span className="flex-1 text-[15px] text-gray-900 dark:text-white">{AIRWAY_CALL.epic}</span>
              <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
            </button>
          </Card>

          <Section title="Units">
            <LocationRows
              data={thiCvor.locations}
              items={units}
              footer={<p className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500 italic">{AIRWAY_CALL.resourcesPending}</p>}
            />
          </Section>

          <Section title="Codes">
            <Card><StepRow items={CODES} /></Card>
          </Section>
        </>
      )}
    </div>
  );
};

export default AirwaysAndCodes;
